import React from "react";
import { Col, Row, Button, Input, UncontrolledTooltip } from "reactstrap";
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import { sendAuditEvent } from "../utils/auditTrail";
import { DEMO_FEATURE_UNAVAILABLE, isDemoLocked } from "../utils/demoConfig";

const demoLockedControl = (tooltipId, control) => (
  <>
    <span id={tooltipId}>{control}</span>
    <UncontrolledTooltip placement="top" trigger="hover" target={tooltipId}>
      {DEMO_FEATURE_UNAVAILABLE}
    </UncontrolledTooltip>
  </>
);

const InvoiceTable = ({ tableData, searchQuery, defaultSorted, isMoveMusicDooUser, auditData }) => {
  const { ExportCSVButton } = CSVExport;

  const currencies = ["€", "$", "£"];

  const amountFormatter = (cell, row) => `${cell}${currencies[row.currency]}`;
  const logInvoiceFileClick = (fileType, fileUrl, invoiceId) => {
    sendAuditEvent({
      token: auditData?.token,
      eventType: 13,
      route: auditData?.route || window.location.pathname,
      entityType: "invoice",
      entityIds: [invoiceId],
      metadata: {
        file_type: fileType,
        file_url: fileUrl,
      },
    });
  };

  const statusFormatter = cell => {
    const status = String(cell ?? "").trim().toLowerCase();

    if (status === "pending") {
      return (
        <Button className="btn-status" disabled color="warning">
          Pending
        </Button>
      );
    }
    if (status === "paid") {
      return (
        <Button className="btn-status" disabled color="success">
          Paid
        </Button>
      );
    }
    if (status === "generating") {
      return (
        <Button className="btn-status" disabled color="warning">
          Generating
        </Button>
      );
    }
    if (status === "failed" || status === "not generated" || status === "error") {
      return (
        <Button className="btn-status" disabled color="danger">
          {cell}
        </Button>
      );
    }
    if (cell) {
      return (
        <Button className="btn-status" disabled color="primary">
          {cell}
        </Button>
      );
    }
    return null;
  };

  const statusCsvFormatter = cell => cell ?? "";

  const downloadFormatter = (cell, row) => {
    const tooltipId = `invoice-pdf-${row.id}`;
    const button = (
      <Button
        className="btn-status"
        color="primary"
        disabled
        style={{ pointerEvents: "none" }}
      >
        <b>Download</b>
      </Button>
    );

    if (isDemoLocked()) {
      return demoLockedControl(tooltipId, button);
    }

    return cell ? (
      <a
        href={cell}
        target="blank"
        rel="noreferrer"
        onClick={() => {
          logInvoiceFileClick("invoice_pdf", cell, row.id);
        }}
      >
        <Button className="btn-status" color="primary">
          <b>Download</b>
        </Button>
      </a>
    ) : null;
  };

  const witholdingTaxStatementFormatter = (cell, row) => {
    const tooltipId = `invoice-wht-${row.id}`;
    const button = (
      <Button
        className="btn-status"
        color="primary"
        disabled
        style={{ pointerEvents: "none" }}
      >
        <b>Download</b>
      </Button>
    );

    if (isDemoLocked()) {
      return demoLockedControl(tooltipId, button);
    }

    return cell ? (
      <a
        href={cell}
        target="blank"
        rel="noreferrer"
        onClick={() => {
          logInvoiceFileClick("withholding_tax_statement_pdf", cell, row.id);
        }}
      >
        <Button className="btn-status" color="primary">
          <b>Download</b>
        </Button>
      </a>
    ) : "Not Applicable";
  };

  const whtStatementCsvFormatter = (cell) => cell || "";

  const columns = [
    {
      dataField: "issue_date",
      text: "Issue Date",
      sort: true,
      headerStyle: () => ({ width: "120px" })
    },
    {
      dataField: "invoice_number",
      text: "Invoice Number",
      sort: true,
      headerStyle: () => ({ width: "120px" })
    },
    {
      dataField: "status",
      text: "Status",
      sort: true,
      formatter: statusFormatter,
      csvFormatter: statusCsvFormatter,
      headerStyle: () => ({ width: "120px" })
    },
    {
      dataField: "total_amount",
      text: "Total Amount",
      sort: true,
      formatter: amountFormatter,
      csvFormatter: amountFormatter,
      headerStyle: () => ({ width: "120px" })
    },
    {
      dataField: "invoice_pdf",
      text: "Download PDF",
      formatter: (cell, row) => downloadFormatter(cell, row),
      headerStyle: () => ({ width: "100px" })
    },
    {
      dataField: "name",
      sort: false,
      csvExport: false,
      hidden: true,
    }
  ];

  if (isMoveMusicDooUser) {
    columns.push({
      dataField: "wht_pdf",
      text: "WHT Statement",
      formatter: (cell, row) => witholdingTaxStatementFormatter(cell, row),
      csvFormatter: whtStatementCsvFormatter,
      headerStyle: () => ({ width: "100px" })
    });
  }

  const handleTableChange = (type, { sortField, sortOrder }) => {
    const ascDesc = sortOrder === "desc" ? "-" : "";
    if (sortField === "issue_date" || sortField === "invoice_number" || sortField === "status" || sortField === "total_amount") {
      searchQuery.setOrdering({ order: ascDesc, name: sortField, sort: sortField });
    }
  };

  return (
    <ToolkitProvider
      keyField="id"
      data={tableData}
      columns={columns}
      search={{
        searchFormatted: true
      }}
      exportCSV={{ fileName: "invoices.csv" }}
    >
      {props => (
        <div>
          <Row>
            <Col>
              <div className="customSearch">
                <Input
                  placeholder="Search..."
                  value={searchQuery.query}
                  style={{ marginBottom: 10 }}
                  onChange={e => searchQuery.setQuery(e.target.value)}
                />
              </div>
            </Col>
            <Col>
              <div className="flexEndContainer">
                <ExportCSVButton className="btn btn-outline-info" {...props.csvProps}>
                  Export CSV
                </ExportCSVButton>
              </div>
            </Col>
          </Row>
          <BootstrapTable
            {...props.baseProps}
            bordered={false}
            remote={{ sort: true }}
            defaultSorted={defaultSorted}
            onTableChange={handleTableChange}
          />
        </div>
      )}
    </ToolkitProvider>
  );
};

export default InvoiceTable;
